import { CompactDialogContent } from '../../ui/CompactDialogContent.js';
import * as Dialog from '@radix-ui/react-dialog';
import type { DeleteCrmShootRequest } from '@diana/contracts';
import { useEffect, useRef, useState } from 'react';
import { useDianaUIPortal } from '../../ui/index.js';

type Preview = {
  revision: number;
  client: { id: string; name: string; revision: number; canDelete: boolean };
};
export function ShootDestructiveDialog({
  mode,
  name,
  revision,
  preview,
  remove,
  cancel,
  close,
}: {
  mode: 'cancel' | 'delete';
  name: string;
  revision: number;
  preview?: () => Promise<Preview>;
  remove?: (input: DeleteCrmShootRequest) => Promise<void>;
  cancel: () => Promise<void>;
  close: () => void;
}) {
  const portal = useDianaUIPortal();
  const initial = useRef({ preview, revision });
  const [data, setData] = useState<Preview | null>(null);
  const [deleteClient, setDeleteClient] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    let active = true;
    if (mode === 'delete') {
      if (!initial.current.preview) {
        setError('Deletion is unavailable.');
        return;
      }
      void initial.current
        .preview()
        .then((value) => {
          if (!active) return;
          if (value.revision !== initial.current.revision) {
            setError('This shoot changed. Close this dialog and reload before deleting.');
            return;
          }
          setData(value);
          setDeleteClient(value.client.canDelete);
        })
        .catch(() => {
          if (active) setError('Could not check linked shoots. Close this dialog and try again.');
        });
    }
    return () => {
      active = false;
    };
  }, [mode]);
  const finish = async () => {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      if (mode === 'cancel') await cancel();
      else {
        if (!data || !remove) return;
        await remove({
          expectedRevision: data.revision,
          deleteClientId: deleteClient ? data.client.id : null,
          expectedClientRevision: deleteClient ? data.client.revision : null,
          confirmed: true,
          confirmation: 'DELETE',
        });
      }
      close();
    } catch {
      setError(
        'Could not confirm the result. Close this dialog and refresh the list before trying again.',
      );
    } finally {
      setBusy(false);
    }
  };
  const deleting = mode === 'delete';
  return (
    <Dialog.Root
      open
      onOpenChange={(open) => {
        if (!open && !busy) close();
      }}
    >
      <Dialog.Portal container={portal}>
        <Dialog.Overlay className="shoot-destructive__overlay" />
        <CompactDialogContent
          title={deleting ? 'Delete shoot?' : 'Cancel shoot?'}
          description={
            <>
              {deleting
                ? `Permanently delete the shoot for ${name}, including its message, notes, checklist and financial details. This cannot be undone.`
                : `Move the shoot for ${name} to Canceled. The shoot and client details will be kept. No message will be sent to the client.`}
            </>
          }
          onClose={close}
          busy={busy}
          footer={
            <div className="shoot-destructive__actions">
              <button
                className="shoot-button shoot-button--outline"
                disabled={busy}
                onClick={close}
              >
                Keep shoot
              </button>
              <button
                className="shoot-button shoot-button--danger"
                disabled={busy || (deleting && (!data || Boolean(error)))}
                onClick={() => void finish()}
              >
                {busy ? 'Working…' : deleting ? 'Delete' : 'Cancel shoot'}
              </button>
            </div>
          }
        >
          {deleting && data ? (
            <label className="shoot-destructive__client">
              <input
                type="checkbox"
                checked={deleteClient}
                disabled={!data.client.canDelete || busy}
                onChange={(event) => setDeleteClient(event.target.checked)}
              />
              Also delete client: {data.client.name}
              <small>
                {data.client.canDelete
                  ? 'This is their only shoot.'
                  : 'Client kept because they have other shoots.'}
              </small>
            </label>
          ) : null}
          {deleting && !data && !error ? <p role="status">Checking linked shoots…</p> : null}
          {error ? <p role="alert">{error}</p> : null}
        </CompactDialogContent>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
